import { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import axios from 'axios'
import { makeNumbersFarsi, makeNumbersLatin } from 'utils/farsi'
import { API_BASE_URL } from 'config/api'
import { ImageUpload } from '../ImageUpload'
import { useAuth } from '../../context/auth-context'
import { useLocation } from 'react-router-dom'

type NotifyFormInputs = {
  productName: string
  author?: string
  vendor?: string
  publisher?: string
  translator?: string
  fullName?: string
  cellphone: string
  email?: string
  image?: string
}

export const NotifyMeForm = ({
  defaultProductName,
  closeShowForm,
}: {
  defaultProductName?: string
  closeShowForm: () => void
}) => {
  const [submitted, setSubmitted] = useState(false)
  const [imageId, setImageId] = useState<string>('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const { user } = useAuth()
  const location = useLocation()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<NotifyFormInputs>({
    defaultValues: { productName: defaultProductName || '' },
  })

  // Restore form data if user is returning from login
  useEffect(() => {
    if (location.state?.from === 'product-request' && user) {
      // Restore all form fields from state
      if (location.state.productName)
        setValue('productName', location.state.productName)
      if (location.state.author) setValue('author', location.state.author)
      if (location.state.publisher)
        setValue('publisher', location.state.publisher)
      if (location.state.translator)
        setValue('translator', location.state.translator)
      if (location.state.fullName) setValue('fullName', location.state.fullName)
      if (location.state.cellphone)
        setValue('cellphone', location.state.cellphone)
      if (location.state.email) setValue('email', location.state.email)
      if (location.state.imageId) setImageId(location.state.imageId)
    }
  }, [location.state, user, setValue])

  const onSubmit = async (data: NotifyFormInputs) => {
    // User must be authenticated to reach this point (checked in Discover component)
    const requestData = {
      ...data,
      image: imageId || undefined,
    }

    await axios
      .post(`${API_BASE_URL}/product-requests`, requestData)
      .then((res) => res.data)

    setSubmitted(true)
    reset()
    setImageId('')
    setUploadError(null)
    closeShowForm()
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='mt-6 max-w-lg mx-auto p-6 rounded shadow relative'
      style={{
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
      }}
    >
      <button
        type='button'
        onClick={closeShowForm}
        className='absolute top-2 right-6 mb-4 text-4xl font-bold'
        style={{ color: 'var(--text-tertiary)' }}
        aria-label='بستن فرم'
      >
        ×
      </button>
      <div className='mb-4 mt-8'>
        <label className='block mb-1 font-medium'>نام کتاب *</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          {...register('productName', { required: 'نام کتاب الزامی است' })}
        />
        {errors.productName && (
          <span className='text-red-500 text-sm'>
            {errors.productName.message}
          </span>
        )}
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>نویسنده</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          {...register('author')}
        />
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>ناشر</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          {...register('publisher')}
        />
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>مترجم</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          {...register('translator')}
        />
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>تصویر کتاب (اختیاری)</label>
        <ImageUpload
          onImageUploaded={(id) => setImageId(id)}
          onError={(error) => setUploadError(error)}
          className=''
        />
        {uploadError && (
          <p className='text-red-500 text-sm mt-2'>{uploadError}</p>
        )}
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>نام کامل</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          {...register('fullName')}
        />
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>شماره موبایل *</label>
        <Controller
          name='cellphone'
          control={control}
          rules={{
            required: 'شماره موبایل الزامی است',
            pattern: {
              value: /^09\d{9}$/,
              message: 'شماره موبایل معتبر نیست',
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div>
              <input
                className='input-theme w-full border rounded px-3 py-2'
                dir='ltr'
                value={makeNumbersFarsi(value || '')}
                onChange={(e) => {
                  const englishValue = makeNumbersLatin(e.target.value)
                  onChange(englishValue)
                }}
              />
              {error && <p className='text-red-500 text-sm'>{error.message}</p>}
            </div>
          )}
        />
      </div>
      <div className='mb-4'>
        <label className='block mb-1 font-medium'>ایمیل</label>
        <input
          className='input-theme w-full border rounded px-3 py-2'
          dir='ltr'
          {...register('email', {
            pattern: {
              value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
              message: 'ایمیل معتبر نیست',
            },
          })}
        />
        {errors.email && (
          <span className='text-red-500 text-sm'>{errors.email.message}</span>
        )}
      </div>
      <button
        type='submit'
        className='w-full font-bold text-xl text-white text-center p-3 rounded flex items-center justify-center'
        style={{ backgroundColor: 'var(--accent-primary)' }}
      >
        ارسال درخواست
      </button>
      {submitted && (
        <div className='text-green-600 text-center mt-4'>
          درخواست شما ثبت شد!
        </div>
      )}
    </form>
  )
}
